import {
  Array,
  Boolean,
  Literal,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

/**
 * Identifies which gateway/provider a chat-completions deployment talks to.
 *
 * Each value here MUST have a `case` in `resolveChatCompletionsEndpoint` in
 * `packages/server/src/magic/providers/providerClientUtils.ts`. That switch
 * is exhaustively typed so adding a new gateway here will fail the build
 * until the credential lookup (and any per-stack gating) is wired up. Today
 * both gateways share `Config.baseten.apiKey` — one Baseten workspace covers
 * serverless inference, dedicated inference, and the Arctic embedding model.
 *
 * This is intentionally separate from `AiClientType`. `AiClientType`
 * describes the high-level *agent* (how Hex talks to a model family);
 * `ChatCompletionsGateway` only disambiguates which OpenAI-compatible
 * gateway sits behind a single `AiClientType.OPENAI_CHAT_COMPLETIONS` agent.
 *
 * Both gateways read the request URL from the deployment config's `baseURL`.
 * The difference is per-stack availability: `BASETEN` (serverless) works on
 * every stack; `BASETEN_DEDICATED` is hard-gated to non-EU stacks at the
 * resolver because the dedicated endpoint is US-only.
 */
export const ChatCompletionsGateway = Union(
  Literal("BASETEN"),
  Literal("BASETEN_DEDICATED"),
);
export type ChatCompletionsGateway = Static<typeof ChatCompletionsGateway>;

/**
 * Agent-side manual compaction for chat-completions deployments that lack
 * provider-native compaction (e.g. Kimi via Baseten).
 */
export const ChatCompletionsManualCompactionConfig = RRecord({
  enabled: Boolean,
  compactThresholdTokens: Number,
});
export type ChatCompletionsManualCompactionConfig = Static<
  typeof ChatCompletionsManualCompactionConfig
>;

export const KIMI_DEFAULT_MANUAL_COMPACTION_CONFIG: ChatCompletionsManualCompactionConfig =
  {
    enabled: true,
    compactThresholdTokens: 180_000,
  };

/**
 * Gateway deployment: API `model` string plus local budgeting fields.
 * Intentionally separate from {@link CustomOpenAiModelConfig} / OpenAI catalog models.
 *
 * `baseURL` is the gateway URL for this deployment (e.g.
 * `https://inference.baseten.co/v1` for serverless, or a
 * `model-<id>.api.baseten.co/...` host for a dedicated deployment).
 * Required at runtime for every gateway today — see
 * `resolveChatCompletionsEndpoint`.
 */
export const ChatCompletionsDeploymentConfig = RRecord({
  model: String,
  maxInputTokens: Number,
  maxOutputTokens: Optional(Number),
  baseURL: Optional(String),
  /**
   * Selects which set of stack credentials to authenticate this deployment.
   * See {@link ChatCompletionsGateway}. Required at the application level;
   * modeled as Optional in the schema only so deployments persisted before
   * this field was introduced still deserialize. The runtime factory
   * (`createAiSdkChatCompletionsClient`) treats a missing value as a hard
   * error rather than silently falling back to a default credential.
   */
  gateway: Optional(ChatCompletionsGateway),
  /**
   * Set to `true` for deployments that accept non-text content parts on
   * `user` messages — `image_url` (vision) and `file` (PDF/document input).
   *
   * When `false` or omitted, the message conformer replaces image/file
   * context and image tool results with short text placeholders rather than
   * dumping base64 as plain text or sending part shapes the gateway will
   * reject.
   *
   * Today there are no deployments that support one but not the other, so a
   * single flag is sufficient. If a future gateway splits these (e.g.
   * accepts PDFs but not images), introduce a separate `supportsFileInput`
   * field rather than overloading this one.
   */
  supportsVision: Optional(Boolean),
  /**
   * Extra fields merged into the `/v1/chat/completions` request body at the
   * top level. Use for non-standard passthrough params accepted by specific
   * gateways — for example Baseten/Kimi's
   * `{ chat_template_kwargs: { enable_thinking: true } }` to enable thinking.
   * Mirrors the Python OpenAI SDK's `extra_body` argument.
   */
  extraBody: Optional(Unknown),
  /**
   * When set, enables agent-side preflight compaction for this deployment.
   * Reference a named default (e.g. {@link KIMI_DEFAULT_MANUAL_COMPACTION_CONFIG})
   * rather than inlining when possible.
   */
  manualCompactionConfig: Optional(ChatCompletionsManualCompactionConfig),
});
export type ChatCompletionsDeploymentConfig = Static<
  typeof ChatCompletionsDeploymentConfig
>;

/**
 * Parameters for `/v1/chat/completions` against an OpenAI-*compatible* gateway.
 */
export const ChatCompletionsModelParams = RRecord({
  frequencyPenalty: Number,
  n: Number,
  presencePenalty: Number,
  stopWords: Array(String),
  temperature: Optional(Number),
  topP: Number,
  model: ChatCompletionsDeploymentConfig,
});
export type ChatCompletionsModelParams = Static<
  typeof ChatCompletionsModelParams
>;

/**
 * Billable model identifiers for Kimi models routed through the CHAT_COMPLETIONS
 * / Baseten gateway. These are used as keys in the credit pricing table and
 * stored in usage-log records, so values must never be renamed once in use.
 */
export const KimiModelLiteral = Union(
  Literal("KIMI_K_2_6"),
  Literal("KIMI_K_2_6_THINKING"),
);
export type KimiModel = Static<typeof KimiModelLiteral>;
export const KimiModel = getNormalEnum(KimiModelLiteral);
